import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { authAPI } from "../services/authAPI";
import { validatePassword, formatAPIError } from "../utils/validation";
import PasswordInput from "../components/common/PasswordInput";
import Header from "../components/layout/Header";
import "./styles/ResetPassword.css";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email || "";
  const successMessage = location.state?.message || "";

  const [formData, setFormData] = useState({
    email,
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(successMessage);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!email) {
      navigate("/forgot-password", {
        state: { error: "Please start the password reset process from the beginning." },
      });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setFormData((prev) => ({ ...prev, otp: value }));
      if (errors.otp) setErrors((prev) => ({ ...prev, otp: "" }));
    }
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.otp) {
      validationErrors.otp = "Reset code is required";
    } else if (formData.otp.length !== 6) {
      validationErrors.otp = "Please enter a valid 6-digit code";
    }

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      validationErrors.newPassword = passwordError;
    }

    if (!formData.confirmPassword) {
      validationErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      validationErrors.confirmPassword = "Passwords do not match";
    }

    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccess("");

    try {
      const response = await authAPI.resetPassword({
        email: formData.email,
        otp: formData.otp,
        newPassword: formData.newPassword,
      });

      setSuccess(response.data.message || "Password reset successfully!");

      setTimeout(() => {
        navigate("/login", {
          state: {
            message: "Password reset successfully. Please login with your new password.",
            email: formData.email,
          },
        });
      }, 2000);
    } catch (error) {
      const errorMessage = formatAPIError(error);

      if (errorMessage.includes("Invalid or expired OTP")) {
        setErrors({ otp: "Invalid or expired reset code. Please request a new one." });
      } else if (errorMessage.includes("Password must be 8+ characters")) {
        setErrors({
          newPassword:
            "Password must be 8+ characters, include uppercase, number, and special symbol",
        });
      } else if (errorMessage.includes("Cannot reuse last 3 passwords")) {
        setErrors({
          newPassword: "This password was recently used. Please choose a different password.",
        });
      } else if (errorMessage.includes("User not found")) {
        setErrors({ submit: "Account not found. Please try again from the beginning." });
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setErrors({});
    setSuccess("");

    try {
      const response = await authAPI.forgotPassword({ email: formData.email });
      setSuccess(response.data.message || "New reset code sent to your email!");
      setResendCooldown(60);
    } catch (error) {
      const errorMessage = formatAPIError(error);
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (!email) return null;

  return (
    <>
      <Header />

      <div className="rp-page">
        <div className="rp-card">
          <div className="rp-top">
            <div className="rp-icon">
              {/* inline lock icon */}
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M7 10V8a5 5 0 0 1 10 0v2"
                  stroke="#2563EB"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M7 10h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
                  stroke="#2563EB"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h1 className="rp-title">Reset Your Password</h1>
            <p className="rp-subtitle">Please enter your new password</p>
          </div>

          <div className="rp-body">
            {errors.submit && <div className="rp-alert rp-alert--error">{errors.submit}</div>}
            {success && <div className="rp-alert rp-alert--success">{success}</div>}

            <form onSubmit={handleSubmit}>
              {/* OTP (your backend requires it) */}
              <div className="rp-field">
                <label className="rp-label">Reset Code</label>
                <input
                  className={errors.otp ? "rp-otp rp-otp--error" : "rp-otp"}
                  inputMode="numeric"
                  name="otp"
                  value={formData.otp}
                  onChange={handleOTPChange}
                  placeholder="Enter 6-digit code"
                  disabled={loading}
                />
                {errors.otp && <div className="rp-error">{errors.otp}</div>}

                <button
                  type="button"
                  className="rp-resend"
                  onClick={handleResendCode}
                  disabled={loading || resendCooldown > 0}
                >
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
                </button>
              </div>

              <div className="rp-field">
                <label className="rp-label">New Password</label>
                <PasswordInput
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  disabled={loading}
                  className={errors.newPassword ? "rp-input rp-input--error" : "rp-input"}
                />
                {errors.newPassword && <div className="rp-error">{errors.newPassword}</div>}
              </div>

              <div className="rp-field">
                <label className="rp-label">Confirm New Password</label>
                <PasswordInput
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  disabled={loading}
                  className={errors.confirmPassword ? "rp-input rp-input--error" : "rp-input"}
                />
                {errors.confirmPassword && (
                  <div className="rp-error">{errors.confirmPassword}</div>
                )}
              </div>

              <div className="rp-req">
                <div className="rp-req-title">Password Requirements:</div>
                <ul className="rp-req-list">
                  <li>At least 8 characters long</li>
                  <li>Contains uppercase and lowercase letters</li>
                  <li>Contains at least one number</li>
                </ul>
              </div>

              <button type="submit" className="rp-btn rp-btn--primary" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </button>

              <div className="rp-footer">
                <Link to="/login" className="rp-backlink">
                  Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
